import { useMemo, useState } from "react";
import { Loader2, Plus, RefreshCw, Target, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import {
  GOAL_TARGET_STATUS_LABEL,
  type PartnerGoal,
  type PartnerGoalTarget,
} from "@colab/shared";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { EmptyState } from "@/components/shared/empty-state";
import { formatDate } from "@/lib/utils";
import { defaultPeriodFor } from "./goals-period";
import { useUpsertGoalTarget } from "./use-upsert-goal-target";
import { useUpdateGoalTarget } from "./use-update-goal-target";
import { useDeleteGoalTarget } from "./use-delete-goal-target";
import { useRefreshGoalTarget } from "./use-refresh-goal-target";

export function GoalTargetsEditor({
  goal,
  targets,
  partners,
  canManage,
}: {
  goal: PartnerGoal;
  targets: PartnerGoalTarget[];
  partners: Array<{ id: string; company_name: string }>;
  canManage: boolean;
}) {
  const [addOpen, setAddOpen] = useState(false);

  const partnerName = useMemo(() => {
    const m = new Map(partners.map((p) => [p.id, p.company_name]));
    return (id: string) => m.get(id) ?? "—";
  }, [partners]);

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="font-display text-lg font-semibold tracking-tight">
            Targets
          </h2>
          <p className="text-sm text-muted-foreground">
            {targets.length}{" "}
            {targets.length === 1 ? "target" : "targets"} across all partners
            and periods.
          </p>
        </div>
        {canManage && (
          <Button type="button" size="sm" onClick={() => setAddOpen(true)}>
            <Plus className="mr-1.5 h-3.5 w-3.5" /> Add target
          </Button>
        )}
      </div>

      {targets.length === 0 ? (
        <EmptyState
          icon={Target}
          title="No targets yet"
          description="Add a per-partner per-period target. Recompute the current value once partner data starts landing."
          action={
            canManage ? (
              <Button size="sm" onClick={() => setAddOpen(true)}>
                <Plus className="mr-1.5 h-3.5 w-3.5" /> Add first target
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="overflow-hidden rounded-xl border bg-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/30 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-2.5">Partner</th>
                <th className="px-4 py-2.5">Period</th>
                <th className="px-4 py-2.5 text-right">Target</th>
                <th className="px-4 py-2.5 text-right">Current</th>
                <th className="px-4 py-2.5 text-right">Progress</th>
                <th className="px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {targets.map((t) => (
                <TargetRow
                  key={t.id}
                  target={t}
                  goalId={goal.id}
                  partnerName={partnerName(t.partner_id)}
                  canManage={canManage}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AddTargetDialog
        goal={goal}
        partners={partners}
        open={addOpen}
        onOpenChange={setAddOpen}
      />
    </div>
  );
}

function TargetRow({
  target,
  goalId,
  partnerName,
  canManage,
}: {
  target: PartnerGoalTarget;
  goalId: string;
  partnerName: string;
  canManage: boolean;
}) {
  const updateMutation = useUpdateGoalTarget();
  const deleteMutation = useDeleteGoalTarget();
  const refreshMutation = useRefreshGoalTarget();

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(target.target_value));
  const [override, setOverride] = useState<string>(
    target.current_value_override == null
      ? ""
      : String(target.current_value_override),
  );

  const effective = target.current_value_override ?? target.current_value;
  const pct =
    target.target_value > 0
      ? Math.min(
          100,
          Math.round((Number(effective) / target.target_value) * 100),
        )
      : 0;

  function onSave() {
    const targetNum = Number(draft);
    if (!Number.isFinite(targetNum) || targetNum <= 0) {
      toast.error("Target must be a positive number");
      return;
    }
    const overrideNum = override.trim() === "" ? null : Number(override);
    if (overrideNum !== null && !Number.isFinite(overrideNum)) {
      toast.error("Override must be a number or empty");
      return;
    }
    updateMutation.mutate(
      {
        id: target.id,
        goalId,
        partnerId: target.partner_id,
        patch: {
          target_value: targetNum,
          current_value_override: overrideNum,
        },
      },
      {
        onSuccess: () => {
          toast.success("Saved");
          setEditing(false);
        },
      },
    );
  }

  function onClearOverride() {
    if (target.current_value_override == null) return;
    updateMutation.mutate({
      id: target.id,
      goalId,
      partnerId: target.partner_id,
      patch: { current_value_override: null },
    });
  }

  return (
    <tr className="border-b last:border-b-0 hover:bg-muted/30">
      <td className="px-4 py-3 font-medium">{partnerName}</td>
      <td className="px-4 py-3 text-muted-foreground">
        {formatDate(target.period_start)} → {formatDate(target.period_end)}
      </td>
      <td className="px-4 py-3 text-right tabular-nums">
        {editing && canManage ? (
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="h-7 w-24 text-right"
            inputMode="numeric"
          />
        ) : (
          target.target_value.toLocaleString()
        )}
      </td>
      <td className="px-4 py-3 text-right tabular-nums">
        {editing && canManage ? (
          <div className="flex items-center justify-end gap-1">
            <Input
              value={override}
              onChange={(e) => setOverride(e.target.value)}
              placeholder="auto"
              className="h-7 w-24 text-right"
              inputMode="numeric"
            />
          </div>
        ) : (
          <div className="flex items-center justify-end gap-1.5">
            {target.current_value_override != null && (
              <Badge variant="outline" className="text-[10px]">
                Override
              </Badge>
            )}
            <span>{Number(effective).toLocaleString()}</span>
            {!editing && target.current_value_override != null && canManage && (
              <button
                type="button"
                onClick={onClearOverride}
                className="text-muted-foreground hover:text-foreground"
                aria-label="Clear override"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
        )}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-end gap-2">
          <Progress value={pct} className="w-16" />
          <span className="w-10 text-right text-xs tabular-nums">{pct}%</span>
        </div>
      </td>
      <td className="px-4 py-3 text-muted-foreground">
        {GOAL_TARGET_STATUS_LABEL[target.status]}
      </td>
      <td className="px-4 py-3 text-right">
        {canManage &&
          (editing ? (
            <div className="flex items-center justify-end gap-1">
              <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
                Cancel
              </Button>
              <Button size="sm" onClick={onSave}>
                Save
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-end gap-1">
              <Button
                size="sm"
                variant="ghost"
                onClick={() =>
                  refreshMutation.mutate(
                    {
                      id: target.id,
                      goalId,
                      partnerId: target.partner_id,
                    },
                    { onSuccess: () => toast.success("Recomputed") },
                  )
                }
                disabled={refreshMutation.isPending}
              >
                <RefreshCw className="h-3.5 w-3.5" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setEditing(true)}
              >
                Edit
              </Button>
              <button
                type="button"
                className="rounded-md p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                onClick={() => {
                  if (!confirm("Delete this target?")) return;
                  deleteMutation.mutate(
                    {
                      id: target.id,
                      goalId,
                      partnerId: target.partner_id,
                    },
                    { onSuccess: () => toast.success("Target deleted") },
                  );
                }}
                aria-label="Delete target"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
      </td>
    </tr>
  );
}

function AddTargetDialog({
  goal,
  partners,
  open,
  onOpenChange,
}: {
  goal: PartnerGoal;
  partners: Array<{ id: string; company_name: string }>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const mutation = useUpsertGoalTarget();
  const fallback = defaultPeriodFor(goal.frequency) ?? {
    start: new Date().toISOString().slice(0, 10),
    end: new Date().toISOString().slice(0, 10),
  };
  const [partnerId, setPartnerId] = useState(goal.partner_id ?? "");
  const [periodStart, setPeriodStart] = useState(fallback.start);
  const [periodEnd, setPeriodEnd] = useState(fallback.end);
  const [targetValue, setTargetValue] = useState("");

  function reset() {
    setPartnerId(goal.partner_id ?? "");
    setPeriodStart(fallback.start);
    setPeriodEnd(fallback.end);
    setTargetValue("");
  }

  function onSubmit() {
    if (!partnerId) {
      toast.error("Pick a partner");
      return;
    }
    const tv = Number(targetValue);
    if (!Number.isFinite(tv) || tv <= 0) {
      toast.error("Target must be a positive number");
      return;
    }
    mutation.mutate(
      {
        goal_id: goal.id,
        partner_id: partnerId,
        period_start: periodStart,
        period_end: periodEnd,
        target_value: tv,
      },
      {
        onSuccess: () => {
          toast.success("Target saved");
          reset();
          onOpenChange(false);
        },
      },
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">
            Add target
          </DialogTitle>
          <DialogDescription>
            Pick a partner and a target value for a period. Recompute the
            current value once partner activity is recorded.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="t-partner">Partner</Label>
            <Select value={partnerId} onValueChange={setPartnerId}>
              <SelectTrigger id="t-partner">
                <SelectValue placeholder="Pick a partner" />
              </SelectTrigger>
              <SelectContent>
                {partners.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.company_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="t-from">Period start</Label>
              <Input
                id="t-from"
                type="date"
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="t-to">Period end</Label>
              <Input
                id="t-to"
                type="date"
                value={periodEnd}
                onChange={(e) => setPeriodEnd(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="t-val">Target value</Label>
            <Input
              id="t-val"
              value={targetValue}
              onChange={(e) => setTargetValue(e.target.value)}
              inputMode="numeric"
              placeholder="e.g. 10"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={onSubmit}
            disabled={mutation.isPending}
          >
            {mutation.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
